import i18next from 'i18next';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import BaseTableOptionsDropdown from 'components/atoms/base-table-option-dropdown';
import { DOCUMENTATION_TIME_PLACEHOLDER } from 'infrastructure/consts/tables';
import { convertSecondsToTime } from 'utils/timeHelper';
import { tableSortMap } from 'infrastructure/antd/functions/create-sort-order';

import type { SortState } from 'infrastructure/types';
import type { IBaseTableColumns } from 'components/atoms/base-table/types';
import type {
  ICondition,
  IConditionForUpdate,
  IConditionIcdCode,
} from 'infrastructure/interfaces';

interface Props {
  setConditionForUpdate: (args: IConditionForUpdate) => void;
  toggleOpen: () => void;
  handleDelete: (guid: string) => void;
  loading: boolean;
  updatedItemGuid?: string;
  disableActions?: boolean;
  documentationTimeSort: SortState;
  dateTimeSort: SortState;
}

export const columns = ({
  setConditionForUpdate,
  toggleOpen,
  handleDelete,
  loading,
  updatedItemGuid,
  disableActions,
  documentationTimeSort,
  dateTimeSort,
}: Props): IBaseTableColumns<ICondition> => {
  return [
    {
      label: i18next.t('labels.dateTime'),
      key: 'updatedAt',
      width: '170px',
      render(date: string) {
        return DateFormatter({
          date,
          format: DateFormats['MM/dd/yyyy, hh:mm:ss a'],
        });
      },
      dataCy: 'conditions-goal-date',
      sortable: true,
      sortKey: 'updatedAt',
      sortOrder: tableSortMap[dateTimeSort],
    },
    {
      label: i18next.t('labels.documentTime'),
      key: 'documentationTime',
      align: 'left',
      width: '170px',
      render(documentationTime?: number | null) {
        const actionTime = !documentationTime
          ? DOCUMENTATION_TIME_PLACEHOLDER
          : convertSecondsToTime(documentationTime);
        return (
          <span data-cy="conditions-goal-documentation-time">{actionTime}</span>
        );
      },
      sortable: true,
      sortKey: 'documentationTime',
      sortOrder: tableSortMap[documentationTimeSort],
    },
    {
      label: i18next.t('labels.icdCode'),
      key: 'icdCode',
      width: '170px',
      render(icdCode: IConditionIcdCode) {
        return icdCode.code;
      },
      dataCy: 'conditions-goal-icd-code',
    },
    {
      label: i18next.t('labels.description'),
      key: 'description',
      width: '600px',
      responsive: ['lg'],
      dataCy: 'conditions-goal-description',
    },
    {
      label: '',
      width: '20px',
      align: 'left',
      key: 'guid',
      render(guid: string, record) {
        return (
          <BaseTableOptionsDropdown
            loading={loading && guid === updatedItemGuid}
            onEdit={() => {
              toggleOpen();
              setConditionForUpdate({
                code: record.icdCode.code,
                description: record.description,
                diagnoseGuid: record.icdCode.guid,
                patientGuid: record.patient.guid,
                guid,
              });
            }}
            onDelete={() => {
              setConditionForUpdate({ guid });
              handleDelete(guid);
            }}
            disabled={disableActions}
          />
        );
      },
    },
  ];
};
